import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import { Calendar } from "react-date-range"
import { useState, useEffect } from "react"
import { Grid, Modal } from "@mui/material"
import MDBox from "components/MDBox"
import moment from "moment"
import MDTypography from "components/MDTypography"
import { noteTypography } from "shared/styles/style"
import { enUS } from "date-fns/locale"
import { startOfYear, endOfYear, addYears, getDay } from "date-fns"

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "fit-content",
  bgcolor: "background.paper",
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: 24,
  outline: 0,
  maxWidth: 400,
}

export const DateRangeSelect = ({
  onChangeStartDate,
  onChangeEndDate,
  disable,
  defaultStartDate,
  defaultEndDate,
}) => {
  const validDefaultStartDate =
    defaultStartDate && moment(defaultStartDate, "MM-DD-YYYY", true).isValid()
      ? moment(defaultStartDate, "MM-DD-YYYY").toDate()
      : moment().startOf("isoweek").toDate()

  const validDefaultEndDate =
    defaultEndDate && moment(defaultEndDate, "MM-DD-YYYY", true).isValid()
      ? moment(defaultEndDate, "MM-DD-YYYY").toDate()
      : moment().endOf("isoweek").toDate()

  const [startDate, setStartDate] = useState(validDefaultStartDate)
  const [endDate, setEndDate] = useState(validDefaultEndDate)
  const [isStartOpen, setStartOpen] = useState(false)
  const [isEndOpen, setEndOpen] = useState(false)

  // Notify parent component when start date changes
  useEffect(() => {
    if (onChangeStartDate) {
      onChangeStartDate(startDate)
    }
  }, [startDate])

  // Notify parent component when end date changes
  useEffect(() => {
    if (onChangeEndDate) {
      onChangeEndDate(endDate)
    }
    // Note: onChangeEndDate is intentionally omitted from dependencies to avoid infinite loops

  }, [endDate])

  const minDate = startOfYear(addYears(new Date(), -4))
  const maxDate = endOfYear(addYears(new Date(), 2))

  return (
    <MDBox display="flex" flexDirection="column" gap={1} width="100%">
      {/* Start Date Picker */}
      <MDBox>
        <MDTypography
          sx={({ breakpoints }) => ({
            ...noteTypography(breakpoints),
            fontSize: "0.75rem",
            fontWeight: 500,
            marginBottom: "4px",
            color: "#344767",
          })}
        >
          Start Date
        </MDTypography>
        <Grid
          container
          alignItems="center"
          sx={({ breakpoints, functions: { pxToRem } }) => ({
            borderRadius: pxToRem(8),
            border: "1px solid #d2d6da",
            background: "#fff",
            width: "100%",
            minWidth: "160px",
            cursor: disable ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: disable ? "#d2d6da" : "#1A73E8",
            },
          })}
          onClick={() => {
            if (!disable) setStartOpen(true)
          }}
        >
          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={({ functions: { pxToRem } }) => ({
              height: pxToRem(40),
              padding: `0 ${pxToRem(12)}`,
            })}
          >
            <MDTypography
              sx={({ breakpoints }) => ({
                ...noteTypography(breakpoints),
                lineHeight: 1.5,
                fontSize: "0.875rem",
                color: "#344767",
              })}
            >
              {moment(startDate).format("MMM DD, YYYY")}
            </MDTypography>
          </Grid>
        </Grid>
      </MDBox>

      {/* End Date Picker */}
      <MDBox>
        <MDTypography
          sx={({ breakpoints }) => ({
            ...noteTypography(breakpoints),
            fontSize: "0.75rem",
            fontWeight: 500,
            marginBottom: "4px",
            color: "#344767",
          })}
        >
          End Date
        </MDTypography>
        <Grid
          container
          alignItems="center"
          sx={({ breakpoints, functions: { pxToRem } }) => ({
            borderRadius: pxToRem(8),
            border: "1px solid #d2d6da",
            background: "#fff",
            width: "100%",
            minWidth: "160px",
            cursor: disable ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: disable ? "#d2d6da" : "#1A73E8",
            },
          })}
          onClick={() => {
            if (!disable) setEndOpen(true)
          }}
        >
          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={({ functions: { pxToRem } }) => ({
              height: pxToRem(40),
              padding: `0 ${pxToRem(12)}`,
            })}
          >
            <MDTypography
              sx={({ breakpoints }) => ({
                ...noteTypography(breakpoints),
                lineHeight: 1.5,
                fontSize: "0.875rem",
                color: "#344767",
              })}
            >
              {moment(endDate).format("MMM DD, YYYY")}
            </MDTypography>
          </Grid>
        </Grid>
      </MDBox>

      {/* Start Date Modal */}
      <Modal
        open={isStartOpen}
        onClose={() => {
          setStartOpen(false)
        }}
      >
        <MDBox sx={{ ...style }}>
          <Calendar
            onChange={(item) => {
              setStartDate(item)
              setStartOpen(false)
            }}
            date={startDate}
            minDate={minDate}
            maxDate={endDate || maxDate}
            // Only allow Mondays to be selected (week start requirement for class scheduling)
            disabledDay={(date) => getDay(date) !== 1}
            locale={enUS}
          />
        </MDBox>
      </Modal>

      {/* End Date Modal */}
      <Modal
        open={isEndOpen}
        onClose={() => {
          setEndOpen(false)
        }}
      >
        <MDBox sx={{ ...style }}>
          <Calendar
            onChange={(item) => {
              setEndDate(item)
              setEndOpen(false)
            }}
            date={endDate}
            minDate={startDate || minDate}
            maxDate={maxDate}
            // Only allow Mondays to be selected (week start requirement for class scheduling)
            disabledDay={(date) => getDay(date) !== 1}
            locale={enUS}
          />
        </MDBox>
      </Modal>
    </MDBox>
  )
}
